import { useLanguage } from '../../context/LanguageContext';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiTrash2, FiPlus, FiMinus, FiShoppingBag, FiTag, FiX, FiBookmark, FiShoppingCart } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import couponService from '../../services/coupon.service';

const CartPage = () => {
  const { t } = useLanguage();
  const { cart, itemCount, subtotal, updateQuantity, removeItem, clearCart, fetchCart, saveForLater, moveToCart, removeSaved, savedCount } = useCart();
  const { isAuthenticated } = useAuth();
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="section-padding text-center">
        <div className="container" style={{ maxWidth: '500px' }}>
          <FiShoppingBag size={64} className="text-muted mb-3" />
          <h3>{t('yourCartEmpty')}</h3>
          <p className="text-muted mb-4">Looks like you haven't added anything to your cart yet.</p>
          <Link to="/products" className="btn btn-ebw-primary btn-lg">
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  const discount = cart.discount || 0;
  const tax = subtotal * 0.08;
  const shipping = subtotal >= 50 ? 0 : 5.99;
  const total = Math.max(0, subtotal + tax + shipping - discount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const { data } = await couponService.apply(couponCode.trim());
      toast.success(data.message);
      setCouponCode('');
      await fetchCart(); // Refresh cart to get updated discount
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      await couponService.remove();
      toast.success('Coupon removed');
      await fetchCart();
    } catch (error) {
      toast.error('Failed to remove coupon');
    }
  };

  return (
    <div className="section-padding">
      <div className="container">
        <h2 className="mb-4">Shopping Cart ({itemCount} items)</h2>

        <div className="row g-4">
          {/* Cart Items */}
          <div className="col-lg-8">
            <div className="dashboard-card">
              {cart.items.map((item) => (
                <div key={item.productId || item._id} className="cart-item">
                  <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <img
                      src={item.image || '/placeholder.png'}
                      alt={item.name}
                      className="cart-item-image"
                    />
                    <div>
                      <h6 className="mb-1">{item.name}</h6>
                      {item.variantName && (
                        <small className="text-muted d-block mb-1">{item.variantName}</small>
                      )}
                      <p className="text-muted small mb-0">${item.price?.toFixed(2)} each</p>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-4">
                    <div className="quantity-control">
                      <button onClick={() => updateQuantity(item.productId, item.quantity - 1)}>
                        <FiMinus size={14} />
                      </button>
                      <span className="fw-semibold px-2">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.productId, item.quantity + 1)}>
                        <FiPlus size={14} />
                      </button>
                    </div>

                    <span className="fw-bold" style={{ minWidth: '70px' }}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>

                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        title="Save for later"
                        onClick={() => { saveForLater(item.productId); toast.info('Saved for later'); }}
                      >
                        <FiBookmark size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeItem(item.productId)}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="text-end mt-3">
                <button className="btn btn-sm btn-outline-secondary" onClick={clearCart}>
                  Clear Cart
                </button>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="col-lg-4">
            <div className="dashboard-card">
              <h5 className="mb-3">Order Summary</h5>

              <div className="d-flex justify-content-between mb-2">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Tax (8%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Shipping</span>
                <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
              </div>

              {/* Discount */}
              {discount > 0 && (
                <div className="d-flex justify-content-between mb-2 text-success">
                  <span>Discount</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              )}

              <hr />

              <div className="d-flex justify-content-between mb-3">
                <strong>Total</strong>
                <strong className="text-primary fs-5">${total.toFixed(2)}</strong>
              </div>

              {/* Coupon Section */}
              {isAuthenticated && (
                <div className="mb-3">
                  {cart.couponCode ? (
                    <div className="d-flex align-items-center justify-content-between p-2 rounded"
                      style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                      <div className="d-flex align-items-center gap-2">
                        <FiTag size={14} className="text-success" />
                        <span className="small fw-semibold text-success">{cart.couponCode}</span>
                        <span className="small text-success">(-${discount.toFixed(2)})</span>
                      </div>
                      <button className="btn btn-sm p-0 border-0" onClick={handleRemoveCoupon} title="Remove coupon">
                        <FiX size={16} className="text-muted" />
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                      />
                      <button
                        className="btn btn-sm btn-ebw-outline"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {couponLoading ? '...' : 'Apply'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {subtotal < 50 && (
                <p className="text-muted small mb-3">
                  Add ${(50 - subtotal).toFixed(2)} more for free shipping!
                </p>
              )}

              {isAuthenticated ? (
                <Link to="/checkout" className="btn btn-ebw-primary w-100 btn-lg">
                  Proceed to Checkout
                </Link>
              ) : (
                <Link to="/login" className="btn btn-ebw-primary w-100 btn-lg">
                  Login to Checkout
                </Link>
              )}

              <Link to="/products" className="btn btn-ebw-outline w-100 mt-2">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Saved for Later */}
      {cart.savedForLater?.length > 0 && (
        <div className="container mt-4 mb-5">
          <h5 style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiBookmark size={18} /> Saved for Later ({cart.savedForLater.length})
          </h5>
          <div className="row g-3 mt-2">
            {cart.savedForLater.map(item => (
              <div key={item.productId} className="col-sm-6 col-md-4 col-lg-3">
                <div className="dashboard-card p-3 h-100">
                  <div className="d-flex gap-3">
                    {item.image ? (
                      <img src={item.image} alt={item.name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} />
                    ) : (
                      <div style={{ width: '60px', height: '60px', borderRadius: '8px', background: '#F3F4F6' }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="mb-1 text-truncate" style={{ fontWeight: 600, fontSize: '13px' }}>{item.name}</p>
                      {item.variantName && <p className="mb-1 text-muted" style={{ fontSize: '11px' }}>{item.variantName}</p>}
                      <span style={{ fontWeight: 700, color: '#B91C1C' }}>${item.price?.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mt-2">
                    <button
                      className="btn btn-sm btn-outline-primary flex-fill"
                      onClick={() => { moveToCart(item.productId); toast.success('Moved to cart'); }}
                    >
                      <FiShoppingCart size={12} className="me-1" /> Move to Cart
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeSaved(item.productId)}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
